import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export interface ContextMenuItem {
  label: string;
  value: string;
  onClick: () => void;
}

export interface ContextMenuProps {
  items: ContextMenuItem[];
  isOpen: boolean;
  onClose: () => void;
  anchorEl: HTMLElement | null;
  placement?: 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right';
  checkedValue?: string | null;
}

export function ContextMenu({
  items,
  isOpen,
  onClose,
  anchorEl,
  placement = 'bottom-left',
  checkedValue,
}: ContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  // Calculate position based on anchor element
  useEffect(() => {
    if (!isOpen || !anchorEl || !menuRef.current) return;

    const updatePosition = () => {
      const anchorRect = anchorEl.getBoundingClientRect();
      const menuRect = menuRef.current!.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let top = 0;
      let left = 0;

      // Determine initial position based on placement
      switch (placement) {
        case 'bottom-left':
          top = anchorRect.bottom + 4;
          left = anchorRect.left;
          break;
        case 'bottom-right':
          top = anchorRect.bottom + 4;
          left = anchorRect.right - menuRect.width;
          break;
        case 'top-left':
          top = anchorRect.top - menuRect.height - 4;
          left = anchorRect.left;
          break;
        case 'top-right':
          top = anchorRect.top - menuRect.height - 4;
          left = anchorRect.right - menuRect.width;
          break;
      }

      // Check if menu would be cut off at the bottom
      const spaceBelow = viewportHeight - anchorRect.bottom;
      const spaceAbove = anchorRect.top;

      if (placement.startsWith('bottom-') && spaceBelow < menuRect.height + 8 && spaceAbove > menuRect.height + 8) {
        // Flip to top if there's more space above
        top = anchorRect.top - menuRect.height - 4;
      } else if (placement.startsWith('top-') && spaceAbove < menuRect.height + 8 && spaceBelow > menuRect.height + 8) {
        // Flip to bottom if there's more space below
        top = anchorRect.bottom + 4;
      }

      // Check if menu would be cut off on the right
      const spaceRight = viewportWidth - left;

      if (spaceRight < menuRect.width) {
        // Right-align if it would overflow
        left = anchorRect.right - menuRect.width;

        // Ensure it doesn't go off the left edge
        if (left < 8) {
          left = 8;
        }
      }

      // Ensure it doesn't go off the left edge
      if (left < 8) {
        left = 8;
      }

      // Ensure it doesn't go off the top edge
      if (top < 8) {
        top = 8;
      }

      setPosition({ top, left });
    };

    // Need to update position after the portal renders to get accurate dimensions
    requestAnimationFrame(() => {
      updatePosition();
    });

    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen, anchorEl, placement]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        anchorEl &&
        !anchorEl.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, anchorEl]);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !anchorEl) {
    return null;
  }

  const handleItemClick = (item: ContextMenuItem) => {
    item.onClick();
    onClose();
  };

  const menuElement = (
    <div
      ref={menuRef}
      className={`context-menu context-menu--${placement}`}
      style={{
        position: 'fixed',
        top: `${position.top}px`,
        left: `${position.left}px`,
      }}
    >
      <div className="context-menu-content">
        {items.map((item) => {
          const isChecked = checkedValue === item.value;
          return (
            <button
              key={item.value}
              type="button"
              className={`context-menu-item ${isChecked ? 'context-menu-item--checked' : ''}`}
              onClick={() => handleItemClick(item)}
            >
              <span className="context-menu-item-check">{isChecked ? '✓' : ''}</span>
              <span className="context-menu-item-label">{item.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );

  return createPortal(menuElement, document.body);
}
